import React, {FocusEvent, useContext} from "react";
import Select from 'react-select';
import {
    EntrepriseCorpsEtat,
    Localisation,
    Lot,
    Operation,
    Phase,
    Reserve,
    Role,
    Situation,
    Statut,
    User
} from "../types/Types";
import {AuthContext} from "../../providers/AuthProvider";
import {FormikErrors, FormikTouched} from "formik";
import {CommonHelper} from "../CommonHelper";
import {useLotReserves, useOperationAvailableUsers, useOperationEnterprises, useOperationLots} from "../../api/hooks";

interface OperationSelectProps<Type> {
    type: 'operation' | 'operation-owned' | 'role' | 'phase' | 'phase-lot' | 'localisation' | 'situation' | 'available-user' | 'status' | 'obtention-adlr' | 'civilite' | 'lot' | 'enterprise' | 'buildings' | 'boolean' | 'statistics-groupby';
    onChange: (newValue?: Type) => void;
    value?: Type;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
    isMulti?: boolean;
    title?: string;
    operationId?: number;
    lotId?: number;
    phaseId?: number;
    touched?: boolean | FormikTouched<any> | FormikTouched<any>[];
    error?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
    ignoreOperationIds?: number[];
    maxItemsSelection?: number;
}

type GenericType = number | string | boolean;

interface GenericEntityType {
    value: GenericType;
    label: string;
}

type DataSourceType =
    Operation
    | Role
    | Phase
    | Localisation
    | Situation
    | Statut
    | Lot
    | EntrepriseCorpsEtat
    | GenericEntityType
    | User;

const MODE_OBTENTION_LIST = [
    {
        value: null,
        label: ' ',
        isDisabled: false
    },
    {
    value: 1,
    label: 'De fait',
    isDisabled: true
    }, {
        value: 2,
        label: 'Attestation de levée des réserves',
    isDisabled: false

    }, {
        value: 3,
        label: 'Lettre Valant Attestation de levée des réserves',
        isDisabled: false
    }
];

const CIVILITE_LIST = [{
    value: 'Gestionnaire',
    label: 'Gestionnaire',
    isDisabled: false
}, {
    value: 'Mme',
    label: 'Mme',
    isDisabled: false
}, {
    value: 'Mr',
    label: 'Mr',
    isDisabled: false
}];

const ROLE_ADMIN_ID = 1000;

type TextSelector = string | ((any) => string);

export const EntitySelect = (props: OperationSelectProps<any>) => {
    const {
        value,
        onChange,
        placeholder = '',
        className,
        onBlur,
        type,
        disabled,
        isMulti,
        title,
        touched,
        error,
        operationId,
        lotId,
        phaseId,
        required,
        ignoreOperationIds,
        maxItemsSelection = 0,
    } = props;
    const {operations, roles, phases, localisations, situations, status, isAdmin, user} = useContext(AuthContext);
    const {data: lots = [] as Lot[]} = useOperationLots(operationId);
    const {data: lotReserves = [] as Reserve[]} = useLotReserves(lotId);
    const {data: enterprises = [] as EntrepriseCorpsEtat[]} = useOperationEnterprises(operationId);
    const {data: availableUsers = [] as User[]} = useOperationAvailableUsers(operationId);

    let dataSource: DataSourceType[] = [];
    let idLabel: string = 'value';
    let textLabel: TextSelector = 'label';
    let multiValue, isOptionDisabled;
    let option = (provided, state) => {
        let backgroundColor;
        if (state.isSelected) {
            backgroundColor = '#003b4a';
        } else if (state.isFocused) {
            backgroundColor = '#dbebbd';
        } else {
            backgroundColor = 'white';
        }

        return {...provided, backgroundColor};
    };

    switch (type) {
        case "operation":
            dataSource = operations.filter(o => !ignoreOperationIds || !ignoreOperationIds.includes(o.refOperation));
            idLabel = 'refOperation';
            textLabel = (o) => CommonHelper.formatOperation(o);
            break;
        case 'operation-owned':
            if (isAdmin()) {
                dataSource = operations;
            } else {
                dataSource = user.aPourRoles.map(a => a.operation);
            }
            dataSource = (dataSource as Operation[]).filter(o => !ignoreOperationIds || !ignoreOperationIds.includes(o.refOperation));
            idLabel = 'refOperation';
            textLabel = (o) => CommonHelper.formatOperation(o);
            break;
        case 'available-user':
            dataSource = [...availableUsers];
            idLabel = 'refUti';
            textLabel = (o) => CommonHelper.formatAcquereur(o);
            break;
        case "role":
            dataSource = roles;
            if (isAdmin()) {
                dataSource = roles;
            } else {
                dataSource = roles.filter(r => r.refRole !== ROLE_ADMIN_ID);
            }
            idLabel = 'refRole';
            textLabel = 'libRole';
            break;
        case "phase":
            dataSource = [...phases];
            idLabel = 'refPhase';
            textLabel = 'libPhase';

            break;
        case "phase-lot":
            dataSource = [...phases].filter(p => [5, 6, 7, 8].includes(p.refPhase));
            idLabel = 'refPhase';
            textLabel = 'libPhase';

            break;
        case "localisation":
            dataSource = localisations;
            idLabel = 'refLocalisation';
            textLabel = 'libLocalisation';

            multiValue = (provided, state) => {
                const opacity = state.isDisabled ? 0.5 : 1;
                const transition = 'opacity 300ms';

                return {...provided, opacity, transition};
            }

            const rasLocalisationIds = lotReserves.filter(r => r.indicRas && !r.indicSupprime && r.phaseRefPhase === phaseId).reduce((localisationIds, reserve) => {
                return [
                    ...reserve.aPourLocalisations.filter(localisationId => !localisationIds.includes(localisationId)).map(r => r.localisationRefLocalisation),
                    ...localisationIds
                ];
            }, []);

            isOptionDisabled = (option) => {
                const {value} = option;

                return rasLocalisationIds.includes(value);
            };

            option = (styles, {data: {value}, isDisabled, isFocused, isSelected}) => {
                let backgroundColor;
                if (isSelected) {
                    backgroundColor = '#003b4a';
                } else if (isFocused) {
                    backgroundColor = '#dbebbd';
                } else {
                    backgroundColor = 'white';
                }
                const isRas = rasLocalisationIds.includes(value);

                return {
                    ...styles,
                    backgroundColor,
                    ...(isRas ? {
                        ':after': {
                            ...styles[':after'],
                            backgroundColor: '#86bc24',
                            color: 'white',
                            borderRadius: 10,
                            content: '"RAS"',
                            marginRight: 30,
                            fontSize: 12,
                            fontWeight: 700,
                            padding: '3px 10px',
                            float: 'right'
                        }
                    } : undefined)
                };
            }
            break;
        case "situation":
            dataSource = situations;
            idLabel = 'refSituation';
            textLabel = 'libSituation';
            break;
        case "status":
            dataSource = [...status];
            idLabel = 'refStatut';
            textLabel = 'libStatut';

            if (!required) {
                dataSource.unshift({
                    refStatut: undefined,
                    libStatut: 'Tous les Statuts'
                } as DataSourceType);
            }

            break;
        case "lot":
            dataSource = [...lots].sort((a, b) => {
                return a.numeroLot.localeCompare(b.numeroLot);
            });
            idLabel = 'refLot';
            textLabel = (lot) => CommonHelper.formatLot(lot);
            break;
        case "enterprise":
            dataSource = [...enterprises];
            idLabel = 'entrepriseCorpsEtatId';
            textLabel = (ec) => CommonHelper.formatEntrepriseCorpEtat(ec);
            break;
        case "buildings":
            dataSource = [...lots].reduce((buildings, lot) => {
                if (lot.batiment && !buildings.includes(lot.batiment)) {
                    buildings.push(lot.batiment)
                }

                return buildings;
            }, []).map(b => ({value: b, label: b})) || [];

            /*if (!required) {
                dataSource.unshift({
                    value: undefined,
                    label: 'Tous les Bâtiments'
                } as DataSourceType);
            }*/
            break;
        case "obtention-adlr":
            dataSource = MODE_OBTENTION_LIST;
            break;
        case "civilite":
            dataSource = CIVILITE_LIST;
            break;
        case "boolean":
            dataSource = [{value: true, label: 'Active'}, {value: false, label: 'Inactive'}];
            break;
        case "statistics-groupby":
            dataSource = [{value: 'phase', label: 'par Phase'}, {value: 'enterprise', label: 'par Entreprise'}, {value: 'trade', label: 'par Corps d\'État'}];
            break;
        default:
            throw new Error(`Type ${type} not defined for EntitySelect`);
    }

    const getObjectFunction = (x) => ({
        value: x[idLabel],
        label: typeof textLabel === 'function' ? textLabel(x) : x[textLabel],
        isDisabled: x.isDisabled
    });
    let objectValue;

    if (isMulti) {
        objectValue = dataSource.filter(x => value.includes(x[idLabel])).map(v => getObjectFunction(v));
    } else {
        const match = dataSource.find(x => x[idLabel] === value);
        objectValue = match ? getObjectFunction(match) : null;
    }

    const displayError = touched && error;

    return (<>
        {title && <label className="form-label">{title}</label>}
        <Select
            isMulti={isMulti}
            placeholder={placeholder}
            className={`${className} ${displayError ? 'has-error' : ''}`}
            classNamePrefix="entity-select"
            isDisabled={disabled}
            isOptionDisabled={(option) => (isMulti && maxItemsSelection && objectValue.length >= maxItemsSelection) || (isOptionDisabled && isOptionDisabled(option)) || option.isDisabled}
            value={objectValue}
            options={dataSource.map(getObjectFunction)}
            onBlur={onBlur}
            onChange={(newValue?: any) => {
                onChange(isMulti ? newValue.map(v => v.value) : newValue?.value);
            }}
            noOptionsMessage={() => 'Aucune option'}
            styles={{
                menu: (provided) => ({
                    ...provided,
                    borderBottom: '1px dotted pink',
                    zIndex: 20,
                }),
                option,
                multiValue
            }}
        />

        {/*<div className="btn-group" role="group" aria-label="Basic radio toggle button group">*/}
        {/*    <input type="radio" className="btn-check" name="btnradio" id="btnradio1" autoComplete="off" checked/>*/}
        {/*    <label className="btn btn-outline-primary" htmlFor="btnradio1">Radio 1</label>*/}

        {/*    <input type="radio" className="btn-check" name="btnradio" id="btnradio2" autoComplete="off"/>*/}
        {/*    <label className="btn btn-outline-primary" htmlFor="btnradio2">Radio 2</label>*/}

        {/*    <input type="radio" className="btn-check" name="btnradio" id="btnradio3" autoComplete="off"/>*/}
        {/*    <label className="btn btn-outline-primary" htmlFor="btnradio3">Radio 3</label>*/}
        {/*</div>*/}

        {displayError && <div className="text-danger font-weight-bold">{error}</div>}
    </>);
};
